import { defineComponent as _defineComponent } from 'vue';
import { storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router/composables";
import draggable from "vuedraggable";
import { useConfig } from "@/composables/config";
import { useHashedUserId } from "@/composables/hashedUserId";
import { convertDropData } from "@/stores/activitySetup";
import { useActivityStore } from "@/stores/activityStore";
import { useEventStore } from "@/stores/eventStore";
import { useUserStore } from "@/stores/userStore";
import InvocationsPanel from "../Panels/InvocationsPanel.vue";
import VisualizationPanel from "../Panels/VisualizationPanel.vue";
import ActivityItem from "./ActivityItem.vue";
import InteractiveItem from "./Items/InteractiveItem.vue";
import NotificationItem from "./Items/NotificationItem.vue";
import UploadItem from "./Items/UploadItem.vue";
import AdminPanel from "@/components/admin/AdminPanel.vue";
import FlexPanel from "@/components/Panels/FlexPanel.vue";
import MultiviewPanel from "@/components/Panels/MultiviewPanel.vue";
import NotificationsPanel from "@/components/Panels/NotificationsPanel.vue";
import SettingsPanel from "@/components/Panels/SettingsPanel.vue";
import ToolPanel from "@/components/Panels/ToolPanel.vue";
// require user to long click before dragging
export default /*#__PURE__*/ _defineComponent({
    __name: 'ActivityBar',
    emits: ["dragstart"],
    setup(__props, { emit }) {
        const DRAG_DELAY = 50;
        const { config, isConfigLoaded } = useConfig();
        const route = useRoute();
        const userStore = useUserStore();
        const { hashedUserId } = useHashedUserId();
        const eventStore = useEventStore();
        const activityStore = useActivityStore();
        const { isAdmin, isAnonymous } = storeToRefs(userStore);
        // activities from store
        const { activities } = storeToRefs(activityStore);
        // drag references
        const dragTarget = ref(null);
        const dragItem = ref(null);
        // drag state
        const isDragging = ref(false);
        // sync built-in activities with cached activities
        activityStore.sync();
        /**
         * Checks if the route of an activity is currently being visited and panels are collapsed
         */
        function isActiveRoute(activityTo) {
            return route.path === activityTo && isActiveSideBar("");
        }
        /**
         * Checks if a panel has been expanded
         */
        function isActiveSideBar(menuKey) {
            return userStore.toggledSideBar === menuKey;
        }
        const isSideBarOpen = computed(() => userStore.toggledSideBar !== "");
        /**
         * Checks if an activity that has a panel should have the `is-active` prop
         */
        function panelActivityIsActive(activity) {
            return isActiveSideBar(activity.id) || (activity.to !== null && isActiveRoute(activity.to));
        }
        /**
         * Evaluates the drop data and keeps track of the drop area
         */
        function onDragEnter(evt) {
            const eventData = eventStore.getDragData();
            if (eventData && !eventStore.multipleDragData) {
                dragTarget.value = evt.target;
                dragItem.value = convertDropData(eventData);
                emit("dragstart", dragItem.value);
            }
            else {
                dragItem.value = null;
            }
        }
        /**
         * Removes the dragged activity when exiting the drop area
         */
        function onDragLeave(evt) {
            if (dragItem.value && dragTarget.value == evt.target) {
                const dragId = dragItem.value.id;
                activities.value = activities.value.filter((a) => a.id !== dragId);
            }
        }
        /**
         * Insert the dragged item into the activities list
         */
        function onDragOver(evt) {
            const target = evt.target.closest(".activity-item");
            if (target && dragItem.value) {
                const targetId = target.id;
                const targetIndex = activities.value.findIndex((a) => `activity-${a.id}` === targetId);
                if (targetIndex !== -1) {
                    const dragId = dragItem.value.id;
                    const targetActivity = activities.value[targetIndex];
                    if (targetActivity && targetActivity.id !== dragId) {
                        const activitiesTemp = activities.value.filter((a) => a.id !== dragId);
                        activitiesTemp.splice(targetIndex, 0, dragItem.value);
                        activities.value = activitiesTemp;
                    }
                }
            }
        }
        /**
         * Tracks the state of activities which expand or collapse the sidepanel
         */
        function onToggleSidebar(toggle = "", to = null) {
            // if an activity's dedicated panel/sideBar is already active
            // but the route is different, don't collapse
            if (toggle && to && !(route.path === to) && isActiveSideBar(toggle)) {
                return;
            }
            userStore.toggleSideBar(toggle);
        }
        watch(() => hashedUserId.value, () => {
            activityStore.sync();
        });
        return { __sfc: true, DRAG_DELAY, config, isConfigLoaded, route, userStore, hashedUserId, eventStore, activityStore, isAdmin, isAnonymous, emit, activities, dragTarget, dragItem, isDragging, isActiveRoute, isActiveSideBar, isSideBarOpen, panelActivityIsActive, onDragEnter, onDragLeave, onDragOver, onToggleSidebar, draggable, InvocationsPanel, VisualizationPanel, ActivityItem, InteractiveItem, NotificationItem, UploadItem, AdminPanel, FlexPanel, MultiviewPanel, NotificationsPanel, SettingsPanel, ToolPanel };
    }
});
